import PageHero from "@/components/PageHero";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Award as AwardIcon, BookMarked, Building, Sparkles } from "lucide-react";

const TYPE_META = {
  "Award": { icon: AwardIcon },
  "Affiliation": { icon: Building },
  "Publication": { icon: BookMarked },
  "Recognition": { icon: Sparkles },
};

export default function Awards() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/awards").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((it) => {
      g[it.type] = g[it.type] || [];
      g[it.type].push(it);
    });
    return g;
  }, [items]);

  return (
    <div data-testid="awards-page">
      <PageHero
        eyebrow="Awards & Achievements"
        title="26 Awards. A career honoured by service."
        subtitle="Awards, affiliations, publications, and recognitions earned across local, district, state, national, and institutional levels."
      />
      <section className="section-y">
        <div className="container-x space-y-16">
          {/* Awards breakdown banner */}
          <div className="bg-[hsl(var(--primary))] text-white p-8 sm:p-10" data-testid="awards-stats-banner">
            <div className="label-eyebrow text-white/80 mb-3">Recognition Breakdown</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[
                { k: "4", l: "Local" },
                { k: "1", l: "District" },
                { k: "8", l: "State" },
                { k: "7", l: "National" },
                { k: "6", l: "Institutional" },
              ].map((s) => (
                <div key={s.l} className="border border-white/20 py-5 text-center">
                  <div className="font-display text-4xl text-[hsl(var(--accent))]">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-widest mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          {Object.keys(grouped).length === 0 && <div className="text-[hsl(var(--fg-muted))]">No items yet.</div>}
          {Object.entries(grouped).map(([type, list]) => {
            const Icon = (TYPE_META[type] || { icon: Sparkles }).icon;
            return (
              <div key={type} data-testid={`awards-group-${type}`}>
                <div className="flex items-center gap-3 mb-6">
                  <Icon className="text-[hsl(var(--accent))]" />
                  <h2 className="font-display text-3xl text-[hsl(var(--primary))]">{type}s</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((it) => (
                    <div key={it.id} className="card-flat" data-testid={`award-${it.id}`}>
                      <div className="text-xs uppercase tracking-widest text-[hsl(var(--fg-muted))]">
                        {it.awarded_by || "—"} {it.year && `· ${it.year}`}
                      </div>
                      <h3 className="font-display text-xl mt-2 text-[hsl(var(--primary))]">{it.title}</h3>
                      {it.description && <p className="text-sm text-[hsl(var(--fg-muted))] mt-2">{it.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
